import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  IconButton,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import comms from '../../comms';

function ChatDialog({
  isOpen,
  onClose,
  socket,
  routeId,
  driverName = 'Driver',
  // A message to send automatically once the dialog is open and history has
  // loaded — used by ContactDispatchSheet's "Request a callback" so the driver
  // sees the exact same sent-message confirmation as typing it manually,
  // instead of a silent side-channel with no visible proof it went anywhere.
  pendingMessage = null,
  onPendingMessageSent,
}) {
  const [messageInput, setMessageInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  // Shown in the composer — send failures used to be console-only.
  const [sendError, setSendError] = useState('');
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  // Load chat history when dialog opens
  const loadChatHistory = useCallback(() => {
    if (!socket || !routeId) {
      console.log('ChatDialog: Cannot load history - no socket or routeId');
      return;
    }

    setIsLoading(true);
    
    socket.emit('chat:getMessages', { routeId: routeId }, (response) => {
      setIsLoading(false);
      
      // Response is an array of messages directly
      if (Array.isArray(response)) {
        const formattedMessages = response.map(msg => ({
          id: msg.id || `${msg.timestamp}-${msg.from}`,
          text: msg.message || msg.text || msg.data,
          timestamp: msg.timestamp,
          // Messages from dispatch to driver (direction 'Out') are received by driver
          type: msg.direction === 'Out' ? 'received' : 'sent',
          from: msg.direction === 'Out' ? (msg.from || 'Dispatch') : driverName
        }));
        
        setChatHistory(formattedMessages.sort((a, b) => 
          new Date(a.timestamp) - new Date(b.timestamp)
        ));
        setHasLoadedHistory(true);
      } else {
        console.log('ChatDialog: No messages or error:', response?.error);
        setHasLoadedHistory(true);
      }
    });
  }, [socket, routeId, driverName]);

  // Load history when dialog opens
  useEffect(() => {
    if (isOpen && !hasLoadedHistory) {
      // Make sure this socket is bound to the driver's vehicle before we talk to the
      // server. The server resolves a socket's vehicle ONLY from the room registration
      // that `joinDriverApp` performs (socketComm.js vehicleForSocket -> rooms[unit]
      // .driverAppID), and rejects chat without it ("Driver socket has no authoritative
      // vehicle identity"). comms attempts this on socket connect, but the socket comes
      // up before login, so the attempt can no-op with no retry — which is why chat
      // worked only for a driver who had opened the Messages modal (previously the sole
      // other caller of joinDriverApp). Idempotent, so re-joining is harmless.
      try { comms.joinDriverApp(); } catch (bindErr) {
        console.warn('ChatDialog: joinDriverApp failed', bindErr);
      }
      setSendError('');
      loadChatHistory();
    }
  }, [isOpen, hasLoadedHistory, loadChatHistory]);

  // Reset loaded state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setHasLoadedHistory(false);
    }
  }, [isOpen]);

  // Set up real-time message listening
  useEffect(() => {
    if (!isOpen || !socket) return;

    const handleNewMessage = (messageData) => {
      // Only process messages for this route
      if (messageData.routeId !== routeId) {
        return;
      }

      const newMessage = {
        id: messageData.id || `${messageData.timestamp}-${messageData.from}`,
        text: messageData.message || messageData.data,
        timestamp: messageData.timestamp,
        type: messageData.direction === 'Out' ? 'received' : 'sent',
        from: messageData.direction === 'Out' ? (messageData.from || 'Dispatch') : driverName
      };

      setChatHistory(prevHistory => {
        // Avoid duplicates
        const exists = prevHistory.some(msg => 
          msg.id === newMessage.id || 
          (msg.text === newMessage.text && 
           Math.abs(new Date(msg.timestamp) - new Date(newMessage.timestamp)) < 1000)
        );
        
        if (!exists) {
          return [...prevHistory, newMessage].sort((a, b) => 
            new Date(a.timestamp) - new Date(b.timestamp)
          );
        }
        return prevHistory;
      });
    };

    socket.on('chat:newMessage', handleNewMessage);

    return () => {
      socket.off('chat:newMessage', handleNewMessage);
    };
  }, [isOpen, socket, routeId, driverName]);

  // Shared send path so a typed message and a pre-filled one (e.g. a callback
  // request) go through the exact same server call and local-history append.
  const sendMessageText = useCallback((text, { onDone } = {}) => {
    if (!text.trim()) {
      onDone?.(false);
      return;
    }
    // Was a silent `return` — the driver typed, hit send, and nothing happened with no
    // explanation anywhere except the console.
    if (!socket || !routeId) {
      setSendError(
        !socket
          ? 'Not connected to dispatch. Check your connection and try again.'
          : 'This trip has no route yet — chat opens once dispatch assigns one.'
      );
      onDone?.(false);
      return;
    }
    setSendError('');

    const messageData = {
      routeId: routeId,
      message: text.trim(),
      from: driverName,
      direction: 'In' // Message coming from driver to dispatch
    };

    socket.emit('driver:sendMessage', messageData, (response) => {
      if (response?.success) {
        // Add message to local history immediately
        const newMessage = {
          id: `${Date.now()}-${driverName}`,
          text: text.trim(),
          timestamp: new Date().toISOString(),
          type: 'sent',
          from: driverName
        };

        setChatHistory(prevHistory => [...prevHistory, newMessage]);
        onDone?.(true);
      } else {
        // The server rejects a send when the socket has no authoritative vehicle
        // identity. Surface it rather than failing silently.
        console.error('ChatDialog: Failed to send message:', response?.error);
        setSendError(response?.error || 'Message could not be sent. Please try again.');
        onDone?.(false);
      }
    });
  }, [socket, routeId, driverName]);

  const handleSendMessage = () => {
    if (!messageInput.trim()) return;
    sendMessageText(messageInput, {
      onDone: (success) => { if (success) setMessageInput(''); },
    });
  };

  // Auto-send a pending message (e.g. "Request a callback") once the dialog is
  // open, connected, and history has loaded — waiting for history avoids the
  // sent message getting overwritten/duplicated by the history load that's
  // already in flight.
  useEffect(() => {
    if (!isOpen || !pendingMessage || !hasLoadedHistory || !socket || !routeId) return;
    sendMessageText(pendingMessage, {
      onDone: () => { onPendingMessageSent?.(); },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pendingMessage, hasLoadedHistory, socket, routeId]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        // dvh, not vh: on iOS the visual viewport shrinks when the keyboard opens (and
        // when Safari's toolbars show), and a vh-sized dialog keeps its full height —
        // pushing the composer and Send button off-screen. dvh tracks the real height.
        sx: { height: '80dvh', maxHeight: '600px' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* component="span": MUI's DialogTitle already renders an <h2>, so a Typography
            defaulting to <h6> nested inside it is invalid HTML and triggers React's
            "validateDOMNesting: <h6> cannot appear as a child of <h2>" warning. Keeping
            variant="h6" preserves the visual size; only the element changes. */}
        <Typography variant="h6" component="span">Chat with Dispatch</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
            {chatHistory.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
                No messages yet. Start a conversation with dispatch.
              </Typography>
            ) : (
              chatHistory.map((msg) => (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    justifyContent: msg.type === 'sent' ? 'flex-end' : 'flex-start',
                    mb: 1
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '75%',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: msg.type === 'sent' ? 'primary.main' : 'grey.200',
                      color: msg.type === 'sent' ? 'white' : 'text.primary'
                    }}
                  >
                    <Typography variant="caption" display="block" sx={{ 
                      fontWeight: 'bold',
                      color: msg.type === 'sent' ? 'rgba(255,255,255,0.8)' : 'text.secondary'
                    }}>
                      {msg.from}
                    </Typography>
                    <Typography variant="body2">{msg.text}</Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block',
                        textAlign: 'right',
                        mt: 0.5,
                        color: msg.type === 'sent' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                      }}
                    >
                      {formatTime(msg.timestamp)}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </DialogContent>
      
      {sendError && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              px: 1,
              py: 0.75,
              borderRadius: 1,
              bgcolor: 'error.light',
              color: 'error.contrastText',
            }}
          >
            {sendError}
          </Typography>
        </Box>
      )}

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Type a message..."
          value={messageInput}
          onChange={(e) => setMessageInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={!socket || !routeId}
        />
        <Button 
          variant="contained" 
          onClick={handleSendMessage}
          disabled={!messageInput.trim() || !socket || !routeId}
          sx={{ minWidth: 'auto', px: 2 }}
        >
          <SendIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChatDialog;
